/**
 * Auth Store
 *
 * Manages authentication state for the application including:
 * - Access and refresh tokens with expiration tracking
 * - User information and business context
 * - Computed authentication status
 * - Persistent storage via localStorage
 *
 * NOTE: Server state (user profile, permissions) should use React Query.
 * This store handles client-only auth state (tokens, session status).
 *
 * @module stores/authStore
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * User roles supported by the platform
 */
export type UserRole = 'OWNER' | 'MANAGER' | 'CASHIER' | 'ACCOUNTANT' | 'PLATFORM_ADMIN';

/**
 * Token pair from authentication
 */
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  /** Token expiration timestamp (Date.now() + expiresIn * 1000) */
  expiresAt: number;
}

/**
 * Basic user info stored locally
 * Derived from JWT claims for quick access without API calls
 */
export interface AuthUser {
  /** User's unique identifier */
  id: string;
  /** Phone number in E.164 format (+234XXXXXXXXXX) */
  phone: string;
  /** User's email address (null if not set) */
  email: string | null;
  /** User's display name (null if not set) */
  name: string | null;
  /** Associated business ID (null if no business) */
  businessId: string | null;
  /** Business name for display (null if no business) */
  businessName: string | null;
  /** Partner ID for white-label context (null if not partner) */
  partnerId: string | null;
  /** User's roles in the system */
  roles: UserRole[];
}

/**
 * Auth state interface
 */
interface AuthState {
  /** Current auth tokens (null if not authenticated) */
  tokens: AuthTokens | null;

  /** Basic user info (null if not authenticated) */
  user: AuthUser | null;

  /** Whether authentication is being validated (initial load) */
  isValidating: boolean;

  /** Whether a token refresh is in progress */
  isRefreshing: boolean;

  /** Whether the current device is trusted (persisted) */
  deviceTrusted: boolean;

  /** Whether the user has set up a PIN (persisted) */
  hasPinSet: boolean;

  /** Last known user name for PIN login screen (persisted) */
  lastKnownUserName: string | null;

  /** Last known phone for PIN login screen (persisted) */
  lastKnownPhone: string | null;
}

/**
 * Auth actions interface
 */
interface AuthActions {
  /**
   * Set auth tokens after successful login/refresh
   * @param tokens - New token pair with expiration
   */
  setTokens: (tokens: AuthTokens) => void;

  /**
   * Set user info after successful login
   * @param user - User info derived from JWT or API
   */
  setUser: (user: AuthUser) => void;

  /**
   * Update user's business info after business setup
   * @param businessId - New business ID
   * @param businessName - New business name
   */
  setBusinessInfo: (businessId: string, businessName: string) => void;

  /**
   * Clear all auth state (logout)
   * Removes tokens, user, and any cached auth data
   */
  logout: () => void;

  /**
   * Set validation status (during initial auth check)
   * @param validating - Whether auth is being validated
   */
  setValidating: (validating: boolean) => void;

  /**
   * Set refresh status (during token refresh)
   * @param refreshing - Whether token refresh is in progress
   */
  setRefreshing: (refreshing: boolean) => void;

  /**
   * Set device trust status
   * @param trusted - Whether the current device is trusted
   */
  setDeviceTrusted: (trusted: boolean) => void;

  /**
   * Set whether the user has a PIN configured
   * @param hasPinSet - Whether PIN is set up
   */
  setHasPinSet: (hasPinSet: boolean) => void;

  /**
   * Set last known user info for PIN login screen
   * @param name - User's display name
   * @param phone - User's phone number
   */
  setLastKnownUser: (name: string | null, phone: string | null) => void;

  /**
   * Check if access token is expired or expiring soon
   * Uses a buffer time to trigger refresh before actual expiry
   * @returns true if token needs refresh
   */
  isTokenExpired: () => boolean;

  /**
   * Check if access token will expire within the given milliseconds
   * @param bufferMs - Buffer time in milliseconds
   * @returns true if token will expire within buffer time
   */
  isTokenExpiringSoon: (bufferMs: number) => boolean;

  /**
   * Get the current access token (or null if not authenticated)
   * @returns Current access token or null
   */
  getAccessToken: () => string | null;

  /**
   * Get the current refresh token (or null if not authenticated)
   * @returns Current refresh token or null
   */
  getRefreshToken: () => string | null;
}

/**
 * Combined auth store type
 */
type AuthStore = AuthState & AuthActions;

/**
 * Buffer time before token expiry to trigger refresh (5 minutes)
 * This ensures we refresh before the token actually expires
 */
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

/**
 * Initial auth state
 */
const initialState: AuthState = {
  tokens: null,
  user: null,
  isValidating: true, // Start as validating until we check persisted state
  isRefreshing: false,
  deviceTrusted: false,
  hasPinSet: false,
  lastKnownUserName: null,
  lastKnownPhone: null,
};

/**
 * Auth store hook
 *
 * Provides authentication state and actions for the application.
 * State is automatically persisted to localStorage and restored on mount.
 *
 * @example
 * ```tsx
 * // In a component
 * const user = useAuthStore((state) => state.user);
 * const logout = useAuthStore((state) => state.logout);
 *
 * // Check authentication with selector
 * const isAuthenticated = useAuthStore(selectIsAuthenticated);
 *
 * // Access outside React (e.g., in API interceptors)
 * const { tokens, logout } = useAuthStore.getState();
 * ```
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      ...initialState,

      // Actions
      setTokens: (tokens) => set({ tokens }),

      setUser: (user) => set({ user, isValidating: false }),

      setBusinessInfo: (businessId, businessName) =>
        set((state) => ({
          user: state.user !== null
            ? { ...state.user, businessId, businessName }
            : null,
        })),

      logout: () =>
        set({
          tokens: null,
          user: null,
          isValidating: false,
          isRefreshing: false,
          deviceTrusted: false,
          hasPinSet: false,
          lastKnownUserName: null,
          lastKnownPhone: null,
        }),

      setValidating: (isValidating) => set({ isValidating }),

      setRefreshing: (isRefreshing) => set({ isRefreshing }),

      setDeviceTrusted: (deviceTrusted) => set({ deviceTrusted }),

      setHasPinSet: (hasPinSet) => set({ hasPinSet }),

      setLastKnownUser: (lastKnownUserName, lastKnownPhone) =>
        set({ lastKnownUserName, lastKnownPhone }),

      isTokenExpired: () => {
        const { tokens } = get();
        if (tokens === null) return true;
        return Date.now() >= tokens.expiresAt - TOKEN_REFRESH_BUFFER_MS;
      },

      isTokenExpiringSoon: (bufferMs) => {
        const { tokens } = get();
        if (tokens === null) return true;
        return Date.now() >= tokens.expiresAt - bufferMs;
      },

      getAccessToken: () => {
        const { tokens } = get();
        return tokens?.accessToken ?? null;
      },

      getRefreshToken: () => {
        const { tokens } = get();
        return tokens?.refreshToken ?? null;
      },
    }),
    {
      name: 'sabibooks-auth',
      storage: createJSONStorage(() => localStorage),
      // Only persist tokens, user, and device trust — not transient state
      partialize: (state) => ({
        tokens: state.tokens,
        user: state.user,
        deviceTrusted: state.deviceTrusted,
        hasPinSet: state.hasPinSet,
        lastKnownUserName: state.lastKnownUserName,
        lastKnownPhone: state.lastKnownPhone,
      }),
      // After rehydration, mark validation as complete
      onRehydrateStorage: () => (state) => {
        if (state !== undefined) {
          state.isValidating = false;
        }
      },
    }
  )
);

/**
 * Selector: Check if user is authenticated
 *
 * User is authenticated if:
 * 1. Tokens exist
 * 2. User info exists
 * 3. Access token is not expired (with buffer)
 *
 * @param state - Auth store state
 * @returns true if user is authenticated
 */
export const selectIsAuthenticated = (state: AuthStore): boolean =>
  state.tokens !== null &&
  state.user !== null &&
  !state.isTokenExpired();

/**
 * Selector: Check if user has completed business setup
 *
 * @param state - Auth store state
 * @returns true if user has an associated business
 */
export const selectHasBusiness = (state: AuthStore): boolean =>
  state.user?.businessId !== null && state.user?.businessId !== undefined;

/**
 * Selector: Check if user has a specific role
 *
 * @param role - Role to check for
 * @returns Selector function that checks for the role
 */
export const selectHasRole =
  (role: UserRole) =>
  (state: AuthStore): boolean =>
    state.user?.roles.includes(role) ?? false;

/**
 * Selector: Get current business ID
 *
 * @param state - Auth store state
 * @returns Business ID or null
 */
export const selectBusinessId = (state: AuthStore): string | null =>
  state.user?.businessId ?? null;

/**
 * Selector: Get current user ID
 *
 * @param state - Auth store state
 * @returns User ID or null
 */
export const selectUserId = (state: AuthStore): string | null =>
  state.user?.id ?? null;

/**
 * Selector: Check if auth state is loading (initial load or refresh)
 *
 * @param state - Auth store state
 * @returns true if auth is being validated or refreshed
 */
export const selectIsLoading = (state: AuthStore): boolean =>
  state.isValidating || state.isRefreshing;
